import { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowLeftFill, EditOutline } from '@lyearn/icons';
import { ActionMenu } from '@lyearn/molecules';
import { Box, Button, LinkButton, Stack } from '@lyearn/ui';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import WorkspaceAccessControlDialog from '@/components/WorkspaceAccessControl/components/WorkspaceAccessControlDialog';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { LearnerProfileActionTypes } from '@/modules/learner/views/LearnerProfile/hooks/useLearnerProfile/actions';
import { LearnerWidgetProps } from '@/modules/learner/views/LearnerProfile/types';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { UserStatusEnum, WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

import useMasqueradeAction from '../../../../hooks/useMasqueradeAction';
import { getLearnerHeaderActionItems } from './utils';

const TYPOGRAPHY_PROPS = { variant: 'body-short-02' as const };

interface RenderHeaderBackButtonParams {
  label: React.ReactNode;
  onClick: () => void;
}

// TODO: Move out
export const renderHeaderBackButton = ({ label, onClick }: RenderHeaderBackButtonParams) => (
  <LinkButton
    className="max-w-320 py-2 px-4 ms-8"
    startIcon={<ArrowLeftFill className="rtl:rotate-180" fillColorToken="icon-secondary" />}
    typography={TYPOGRAPHY_PROPS}
    onClick={onClick}>
    <Box className="truncate" component="span">
      {label}
    </Box>
  </LinkButton>
);

type Props = Pick<LearnerWidgetProps, 'learner' | 'onAction'>;

const LearnerProfileHeader = ({ learner, onAction }: Props) => {
  const { t } = useTranslation();
  const isAdmin = useAdminRoute();
  const navigation = useNavigation();
  const currentUser = useUserContext();
  const { [FeatureFlags.ShowUserProfile]: showUserProfile } = useFeatureFlags();

  const isSelf = learner.id === currentUser.id;
  const isManager = learner.manager?.id === currentUser.id;
  const showAssignButton = learner.status === UserStatusEnum.Active && (isAdmin || isManager);

  const { isAccessible: isWorkspaceAccessible, showPermissionSnackbar } =
    useWorkspaceAccessControl();

  usePageLayout({ title: learner.name, showHeader: false });

  const { canMasquerade } = useMasqueradeAction({
    learnerId: learner.id,
    learnerStatus: learner.status,
  });

  const items = useMemo(
    () => getLearnerHeaderActionItems(t, learner, isSelf, canMasquerade),
    [canMasquerade, isSelf, learner, t],
  );

  const onMenuAction = useCallback(
    (type: LearnerProfileActionTypes) => {
      switch (type) {
        case LearnerProfileActionTypes.ShareLearnerProfile: {
          onAction({ type });
          break;
        }
        case LearnerProfileActionTypes.SetInViewModal: {
          isWorkspaceAccessible(WorkspaceActions.Edit, WorkspaceSubjects.Learner)
            ? onAction({ type, payload: { modalType: 'Profile' } })
            : showPermissionSnackbar('edit', 'learner');
          break;
        }
        case LearnerProfileActionTypes.Masquerade: {
          isWorkspaceAccessible(WorkspaceActions.Edit, WorkspaceSubjects.Learner)
            ? onAction({
                type: LearnerProfileActionTypes.SetInViewModal,
                payload: { modalType: 'Masquerade' },
              })
            : showPermissionSnackbar('masquerade', 'learner');
          break;
        }
        case LearnerProfileActionTypes.Deactivate: {
          isWorkspaceAccessible(WorkspaceActions.Edit, WorkspaceSubjects.Learner)
            ? onAction({
                type: LearnerProfileActionTypes.SetInViewModal,
                payload: { modalType: 'Deactivate' },
              })
            : showPermissionSnackbar('deactivate', 'learner');
          break;
        }
        case LearnerProfileActionTypes.Reactivate: {
          isWorkspaceAccessible(WorkspaceActions.Edit, WorkspaceSubjects.Learner)
            ? onAction({
                type: LearnerProfileActionTypes.SetInViewModal,
                payload: { modalType: 'Reactivate' },
              })
            : showPermissionSnackbar('reactivate', 'learner');
          break;
        }
        case LearnerProfileActionTypes.ResetPassword: {
          isWorkspaceAccessible(WorkspaceActions.Manage, WorkspaceSubjects.Learner)
            ? onAction({
                type: LearnerProfileActionTypes.SetInViewModal,
                payload: { modalType: 'ResetPassword' },
              })
            : showPermissionSnackbar('manage', 'learner');
          break;
        }
        default: {
          break;
        }
      }
    },
    [isWorkspaceAccessible, onAction, showPermissionSnackbar],
  );

  const onAssignClick = useCallback(() => {
    onAction({
      type: LearnerProfileActionTypes.SetInViewModal,
      payload: { modalType: 'Assignment' },
    });
  }, [onAction]);

  const AssignButton = <Button onClick={onAssignClick}>{t('Common:Actions.Assign')}</Button>;

  const ResolvedAssignButton = isAdmin ? (
    <WorkspaceAccessControlDialog
      action={WorkspaceActions.Create}
      actionVerb="create"
      fallback={<Button>{t('Common:Actions.Assign')}</Button>}
      subject={WorkspaceSubjects.Assignment}>
      {AssignButton}
    </WorkspaceAccessControlDialog>
  ) : (
    AssignButton
  );

  const onEditProfileClick = useCallback(() => {
    onAction({
      type: LearnerProfileActionTypes.SetInViewModal,
      payload: { modalType: 'EditSelfProfile' },
    });
  }, [onAction]);

  return (
    <Stack
      alignItems="center"
      className="sticky top-0 z-50 h-60 border-b border-border bg-bg-primary ps-8 pe-20"
      justifyContent="between">
      {renderHeaderBackButton({
        label: learner.name || learner.uniqueId,
        onClick: navigation.back,
      })}
      <Stack gapX={12}>
        {isAdmin ? (
          <ActionMenu<LearnerProfileActionTypes>
            items={items}
            minWidth={20.8}
            onAction={onMenuAction}
          />
        ) : null}
        {showAssignButton ? ResolvedAssignButton : null}
        {showUserProfile && isSelf && !isAdmin ? (
          <Button
            appearance="secondary"
            size="medium"
            startIcon={<EditOutline />}
            variant="outlined"
            onClick={onEditProfileClick}>
            {t('Common:EditProfile')}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default LearnerProfileHeader;
